/**
 * Configurazione del tema di Ant Design
 * Supporto per tema chiaro e scuro
 * Tutti i token disponibili sono documentati qui: https://ant.design/docs/react/customize-theme/
 */

import { theme as antdTheme } from 'antd';
import { brandColors } from './brandColors';
import store from '../redux/store';

// Definizione delle palette di colori
export const brandPalette = brandColors;

// Funzione per ottenere i colori dallo store Redux
const getThemeColors = () => {
    const state = store.getState();
    return state.theme;
};

export const statusTokens = {
    success: getThemeColors().status.success,
    warning: getThemeColors().status.warning,
    error: getThemeColors().status.error,
    info: getThemeColors().status.info,
};

// Scale di colori primari, secondari e complementari
export const primaryShades = {
    primary100: getThemeColors().primary.primary100,
    primary200: getThemeColors().primary.primary200,
    primary300: getThemeColors().primary.primary300,
    primary400: getThemeColors().primary.primary400,
    primary500: getThemeColors().primary.primary500,
    primary600: getThemeColors().primary.primary600,
    primary700: getThemeColors().primary.primary700,
};

export const secondaryShades = {
    secondary100: getThemeColors().secondary.secondary100,
    secondary200: getThemeColors().secondary.secondary200,
    secondary300: getThemeColors().secondary.secondary300,
    secondary400: getThemeColors().secondary.secondary400,
    secondary500: getThemeColors().secondary.secondary500,
    secondary600: getThemeColors().secondary.secondary600,
    secondary700: getThemeColors().secondary.secondary700,
};

export const complementaryShades = {
    complementary100: getThemeColors().complementary.complementary100,
    complementary200: getThemeColors().complementary.complementary200,
    complementary300: getThemeColors().complementary.complementary300,
    complementary400: getThemeColors().complementary.complementary400,
    complementary500: getThemeColors().complementary.complementary500,
    complementary600: getThemeColors().complementary.complementary600,
    complementary700: getThemeColors().complementary.complementary700,
};

export const greyScaleTokens = {
    grey100: getThemeColors().grey.grey100,
    grey200: getThemeColors().grey.grey200,
    grey300: getThemeColors().grey.grey300,
    grey400: getThemeColors().grey.grey400,
    grey500: getThemeColors().grey.grey500,
    grey600: getThemeColors().grey.grey600,
    grey700: getThemeColors().grey.grey700,
};

export const backgroundTokens = {
    primaryBg: getThemeColors().background.primaryBg,
    secondaryBg: getThemeColors().background.secondaryBg,
    greyBg: getThemeColors().background.greyBg,
    whiteBg: getThemeColors().background.whiteBg,
};

// Variant tokens secondo lo style guide
export const variantTokens = {
    primary: getThemeColors().variant.primary,
    secondary: getThemeColors().variant.secondary,
    accent: getThemeColors().variant.accent,
    complementary: getThemeColors().variant.complementary,
    white: getThemeColors().variant.white,
    black: getThemeColors().variant.black,
};

// Configurazioni per i box
export const boxStyles = {
    defaultBox: {
        backgroundColor: primaryShades.primary100,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },
    borderBox: {
        backgroundColor: variantTokens.white,
        border: `1px solid ${greyScaleTokens.grey400}`
    },
    greyBox: {
        backgroundColor: greyScaleTokens.grey100
    },
    raisedBox: {
        backgroundColor: primaryShades.primary100,
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)'
    },
    floatingBox: {
        backgroundColor: primaryShades.primary100,
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)'
    }
};

// Button styles
export const buttonStyles = {
    primary: {
        backgroundColor: primaryShades.primary300,
        borderColor: primaryShades.primary300,
        color: variantTokens.white
    },
    outline: {
        borderColor: primaryShades.primary300,
        color: primaryShades.primary300
    },
    secondary: {
        backgroundColor: secondaryShades.secondary200,
        borderColor: secondaryShades.secondary200
    },
    accent: {
        backgroundColor: complementaryShades.complementary200,
        borderColor: complementaryShades.complementary200,
        color: variantTokens.white
    },
    success: {
        backgroundColor: statusTokens.success,
        borderColor: statusTokens.success,
        color: variantTokens.white
    },
    info: {
        backgroundColor: statusTokens.info,
        borderColor: statusTokens.info,
        color: variantTokens.white
    },
    warning: {
        backgroundColor: statusTokens.warning,
        borderColor: statusTokens.warning,
        color: variantTokens.white
    },
    error: {
        backgroundColor: statusTokens.error,
        borderColor: statusTokens.error,
        color: variantTokens.white
    }
};

// Typography configuration
export const typography = {
    fontFamily: '"Inter", sans-serif',
    fontSizes: {
        h1: '36px',
        h2: '24px',
        h3: '20px',
        h4: '16px',
        h5: '14px',
        label: '12px'
    }
};

// Tokens di base per tema chiaro
export const lightBaseTokens = {
    // Colori principali
    colorPrimary: brandColors.primary || variantTokens.primary,
    colorLink: brandColors.accent,
    colorSuccess: statusTokens.success,
    colorError: statusTokens.error,
    colorWarning: statusTokens.warning,
    colorInfo: statusTokens.info,
    colorLightGray: brandColors.lightGray,
    colorGray: brandColors.gray,

    // Colori di base
    colorTextWhite: variantTokens.white,
    colorTextBlack: variantTokens.black,
    colorBgBase: backgroundTokens.whiteBg,
    colorBorder: greyScaleTokens.grey400,

    // Tipografia
    fontFamily: typography.fontFamily,
    fontSizeBase: 14,
    fontSize: 14,
    lineHeight: 1.5714285714285714,
};

// Tokens di base per tema scuro
export const darkBaseTokens = {
    // Colori principali
    colorPrimary: brandColors.primary || variantTokens.primary,
    colorLink: brandColors.accent,
    colorSuccess: statusTokens.success,
    colorError: statusTokens.error,
    colorWarning: statusTokens.warning,
    colorInfo: statusTokens.info,
    colorLightGray: brandColors.lightGray,
    colorGray: brandColors.gray,

    // Colori di base
    colorTextWhite: variantTokens.white,
    colorTextBlack: variantTokens.black,
    colorBgBase: '#141414',
    colorBorder: greyScaleTokens.grey600,

    // Tipografia
    fontFamily: typography.fontFamily,
    fontSizeBase: 14,
    fontSize: 14,
    lineHeight: 1.5714285714285714,
};

// Integrazione delle palette nel sistema dei token
const createTokensFromPalette = (isDark = false) => {
    const colors = {
        status: isDark ? { success: statusTokens.success, warning: statusTokens.warning, error: statusTokens.error, info: statusTokens.info } : statusTokens,
        primary: isDark ? { primary100: primaryShades.primary100, primary200: primaryShades.primary200, primary300: primaryShades.primary300, primary400: primaryShades.primary400, primary500: primaryShades.primary500, primary600: primaryShades.primary600, primary700: primaryShades.primary700 } : primaryShades,
        secondary: isDark ? { secondary100: secondaryShades.secondary100, secondary200: secondaryShades.secondary200, secondary300: secondaryShades.secondary300, secondary400: secondaryShades.secondary400, secondary500: secondaryShades.secondary500, secondary600: secondaryShades.secondary600, secondary700: secondaryShades.secondary700 } : secondaryShades,
        complementary: isDark ? { complementary100: complementaryShades.complementary100, complementary200: complementaryShades.complementary200, complementary300: complementaryShades.complementary300, complementary400: complementaryShades.complementary400, complementary500: complementaryShades.complementary500, complementary600: complementaryShades.complementary600, complementary700: complementaryShades.complementary700 } : complementaryShades,
        grey: isDark ? { grey100: greyScaleTokens.grey100, grey200: greyScaleTokens.grey200, grey300: greyScaleTokens.grey300, grey400: greyScaleTokens.grey400, grey500: greyScaleTokens.grey500, grey600: greyScaleTokens.grey600, grey700: greyScaleTokens.grey700 } : greyScaleTokens,
        background: isDark ? { primaryBg: backgroundTokens.primaryBg, secondaryBg: backgroundTokens.secondaryBg, greyBg: backgroundTokens.greyBg, whiteBg: backgroundTokens.whiteBg } : backgroundTokens,
        variant: isDark ? { primary: variantTokens.primary, secondary: variantTokens.secondary, accent: variantTokens.accent, complementary: variantTokens.complementary, white: variantTokens.white, black: variantTokens.black } : variantTokens,
    };
    return {
        // Colori principali
        colorPrimary: colors.variant.primary,
        colorSecondary: colors.variant.secondary,
        colorTertiary: colors.variant.accent,
        colorComplementary: colors.variant.complementary,
        colorAccent: colors.variant.accent,

        // Colori di stato
        colorSuccess: colors.status.success,
        colorWarning: colors.status.warning,
        colorError: colors.status.error,
        colorInfo: colors.status.info,

        // Sfondi
        colorBgContainer: isDark ? '#1f1f1f' : colors.background.whiteBg,
        colorBgLayout: isDark ? '#141414' : colors.background.primaryBg,
        colorBgSpotlight: isDark ? '#2d2d2d' : colors.background.greyBg,

        // Testo
        colorText: isDark ? colors.variant.white : colors.variant.black,
        colorTextSecondary: isDark ? colors.grey.grey500 : colors.grey.grey600,
        colorTextDisabled: isDark ? colors.grey.grey600 : colors.grey.grey400,

        // Bordi e riempimenti
        colorBorder: isDark ? colors.grey.grey600 : colors.grey.grey400,
        colorBorderSecondary: isDark ? colors.grey.grey700 : colors.grey.grey200,
        colorFill: isDark ? colors.grey.grey700 : colors.grey.grey100,
        colorFillSecondary: isDark ? colors.grey.grey600 : colors.grey.grey200,
    };
};

// Configurazione dei temi
export const theme = {
    // Tema chiaro (default)
    light: {
        token: { ...lightBaseTokens, ...createTokensFromPalette(false) },
        // Aggiungiamo i token personalizzati che possono essere utilizzati ma non sono definiti in Ant Design
        customToken: {
            colorSecondary: variantTokens.secondary,
            colorTertiary: variantTokens.accent,
            colorComplementary: variantTokens.complementary,
            colorAccent: variantTokens.accent,
            primaryShades: primaryShades,
            secondaryShades: secondaryShades,
            complementaryShades: complementaryShades,
            greyScaleTokens: greyScaleTokens,
            backgroundTokens: backgroundTokens,
            boxStyles,
            buttonStyles,
            typography
        }
    },

    // Tema scuro
    dark: {
        token: { ...darkBaseTokens, ...createTokensFromPalette(true) },
        algorithm: antdTheme.darkAlgorithm,
        // Aggiungiamo i token personalizzati per il tema scuro
        customToken: {
            colorSecondary: variantTokens.secondary,
            colorTertiary: variantTokens.accent,
            colorComplementary: variantTokens.complementary,
            colorAccent: variantTokens.accent,
            primaryShades: primaryShades,
            secondaryShades: secondaryShades,
            complementaryShades: complementaryShades,
            greyScaleTokens: greyScaleTokens,
            backgroundTokens: backgroundTokens,
            boxStyles,
            buttonStyles,
            typography
        }
    }
};

// Funzione per ottenere il tema in base alla modalità
export const getTheme = (isDarkMode = false) => {
    return isDarkMode ? theme.dark : theme.light;
};

// Funzione per ottenere i colori personalizzati
export const getCustomColors = (isDarkMode = false) => {
    return isDarkMode ? theme.dark.customToken : theme.light.customToken;
};

// Esporta la configurazione del font per assicurare coerenza in tutta l'applicazione
export const fontFamily = {
    base: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    headings: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    monospace: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
};